import firestore from 'services/firestore';
import { collection, doc, setDoc } from 'firebase/firestore';
import { validateMultipleFields } from 'utils/formValidation';
import {
  REGEX_EMAIL,
  REGEX_COUNTRY_CODE,
  REGEX_DUTCH_ZIPCODE,
  REGEX_PHONE,
  REGEX_ALPHABETICAL_SPACE,
  REGEX_DUTCH_HOUSENUMBER,
  REGEX_ALPHANUMERICAL_SPACE,
  ALLOWED_IMAGE_TYPES,
} from 'utils/validation';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
  listAll,
} from 'firebase/storage';
import storage from 'services/storage';
import uniqueFilename from 'utils/uniqueFilename';
import moment from 'moment';

class Project {
  static validateFirstname(value) {
    if (value.trim() === '') {
      return 'firstname.empty';
    }
    if (!REGEX_ALPHABETICAL_SPACE.test(value)) {
      return 'firstname.invalid';
    }
    if (value.length > 256) {
      return 'firstname.length';
    }
    return '';
  }

  static validateInsertion(value) {
    if (value && !REGEX_ALPHABETICAL_SPACE.test(value)) {
      return 'insertion.invalid';
    }
    if (value.length > 128) {
      return 'insertion.length';
    }
    return '';
  }

  static validateLastname(value) {
    if (value.trim() === '') {
      return 'lastname.empty';
    }
    if (!REGEX_ALPHABETICAL_SPACE.test(value)) {
      return 'lastname.invalid';
    }
    if (value.length > 128) {
      return 'lastname.length';
    }
    return '';
  }

  static validateEmail(value) {
    if (value.trim() === '') {
      return 'email.empty';
    }
    if (!REGEX_EMAIL.test(value)) {
      return 'email.invalid';
    }
    return '';
  }

  static validateCountryCode(value) {
    if (value.trim() === '') {
      return 'countryCode.empty';
    }

    if (!REGEX_COUNTRY_CODE.test(value)) {
      return 'countryCode.invalid';
    }
    return '';
  }

  static validateMobilePhone(value) {
    if (value.trim() === '') {
      return 'mobilePhone.empty';
    }

    const normalized = value.replace(/[\s-]/g, '');

    if (!REGEX_PHONE.test(normalized)) {
      return 'mobilePhone.invalid';
    }
    return '';
  }

  static validateStreetname(value) {
    if (value.trim() === '') {
      return 'streetname.empty';
    }
    if (!REGEX_ALPHABETICAL_SPACE.test(value)) {
      return 'streetname.invalid';
    }
    if (value.length > 95) {
      return 'streetname.length';
    }
    return '';
  }

  static validateHousenumber(value) {
    if (value.trim() === '') {
      return 'housenumber.empty';
    }
    if (!REGEX_DUTCH_HOUSENUMBER.test(value)) {
      return 'housenumber.invalid';
    }
    return '';
  }

  static validateZipcode(value) {
    if (value.trim() === '') {
      return 'zipcode.empty';
    }
    if (!REGEX_DUTCH_ZIPCODE.test(value)) {
      return 'zipcode.invalid';
    }
    return '';
  }

  static validateCity(value) {
    if (value.trim() === '') {
      return 'city.empty';
    }
    if (!REGEX_ALPHABETICAL_SPACE.test(value)) {
      return 'city.invalid';
    }
    if (value.length > 100) {
      return 'city.length';
    }
    return '';
  }

  static validateTitle(value) {
    if (value.trim() === '') {
      return 'title.empty';
    }
    if (!REGEX_ALPHANUMERICAL_SPACE.test(value)) {
      return 'title.invalid';
    }
    if (value.length > 30) {
      return 'title.length';
    }
    return '';
  }

  static validateDescription(value) {
    if (value.trim() === '') {
      return 'description.empty';
    }
    if (!REGEX_ALPHANUMERICAL_SPACE.test(value)) {
      return 'description.invalid';
    }
    if (value.length > 1000) {
      return 'description.length';
    }
    return '';
  }

  static validateImages(value) {
    if (typeof value !== 'object') {
      return 'images.invalid';
    }
    // convert the FileList object to an array, so that we can use the "every" method
    const fileList = Array.from(value);

    if (!fileList.every((file) => ALLOWED_IMAGE_TYPES.includes(file.type))) {
      return 'images.invalid';
    }

    if (fileList.length > 5) {
      return 'images.length';
    }

    return '';
  }

  static validateField(fieldName, value) {
    switch (fieldName) {
      case 'firstname':
        return this.validateFirstname(value);

      case 'insertion':
        return this.validateInsertion(value);

      case 'lastname':
        return this.validateLastname(value);

      case 'email':
        return this.validateEmail(value);

      case 'countryCode':
        return this.validateCountryCode(value);

      case 'mobilePhone':
        return this.validateMobilePhone(value);

      case 'streetname':
        return this.validateStreetname(value);

      case 'housenumber':
        return this.validateHousenumber(value);

      case 'zipcode':
        return this.validateZipcode(value);

      case 'city':
        return this.validateCity(value);

      case 'title':
        return this.validateTitle(value);

      case 'description':
        return this.validateDescription(value);

      case 'images':
        return this.validateImages(value);

      default:
        break;
    }
  }

  /**
   * Images need to be uploaded to storage at /projectId before the project doc can be created.
   * This because we need to imageUrls in the project doc.
   * To solve this we create an empty document which has an id, we save images at projects/NEW_ID,
   * generate the imageUrls and fill the empty document with the project data (happens in useCreateProjectInDB).
   * @static
   * @returns {DocumentReference} reference to empty document in /project/NEW_ID
   */
  static newDocRef() {
    return doc(collection(firestore, 'projects'));
  }

  static toFirebaseObject(data, businessId) {
    if (!data) {
      throw new Error('Data is undefined');
    }

    if (!businessId) {
      throw new Error('BusinessId is undefined');
    }

    // Validate data.
    const validationResponse = validateMultipleFields({
      fields: Object.keys(data),
      data,
      language: 'en',
    });

    // Handle validation errors.
    if (validationResponse) {
      const responseString = Object.values(validationResponse).join(', ');
      throw new Error(`Failed to create firebase object: ${responseString}`);
    }

    // Return the firebase ready doc.
    return (({
      firstname,
      insertion,
      lastname,
      email,
      countryCode,
      mobilePhone,
      housenumber,
      streetname,
      zipcode,
      city,
      ...rest
    }) => ({
      customer: {
        firstname,
        insertion,
        lastname,
        email,
        countryCode,
        mobilePhone,
      },
      adres: {
        housenumber,
        streetname,
        zipcode,
        city,
      },
      ...rest,
      status: 'available',
      businessRef: doc(collection(firestore, 'businesses'), businessId),
      updatedAt: moment().format(),
      createdAt: moment().format(),
    }))(data);
  }

  static async uploadProjectImages(images, projectId) {
    if (!images) {
      throw new Error('Failed to upload project images: images is undefined');
    }

    if (!projectId) {
      throw new Error('Failed to upload project images: projectId is undefined');
    }

    // Uploaded the images to storage.
    const uploadPromises = Array.from(images).map((image) =>
      uploadBytes(
        ref(storage, `/projects/${projectId}/${uniqueFilename(image.name)}`),
        image,
        { contentType: image.type }
      )
    );

    // Wait for all uploads to complete.
    const resolvedUploadPromises = await Promise.all(uploadPromises);

    // Create objects containing url and path for every image.
    const formatPromises = resolvedUploadPromises.map(async (image) => ({
      url: await getDownloadURL(image.ref),
      path: image.ref.fullPath
    }));

    const resolvedFormatPromises = await Promise.all(formatPromises);

    return resolvedFormatPromises;
  }

  static async deleteUploadedImages(projectId) {
    try {
      const storageRef = ref(storage, `/projects/${projectId}`);
      const imagesList = await listAll(storageRef);
      const deletePromises = imagesList.items.map((image) =>
        deleteObject(image)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error(`Failed to delete images: ${error.message}`);
    }
  }

  static async createInDB(data, businessId) {
    const projectDocRef = this.newDocRef();
    try {
      if (!businessId) {
        throw new Error('Failed to create project in db: businessId is undefined');
      }

      const project = this.toFirebaseObject(data, businessId);

      if (Object.keys(data.images).length > 0) {
        project.images = await this.uploadProjectImages(
          data.images,
          projectDocRef.id
        );
      }

      return setDoc(projectDocRef, project);
    } catch (error) {
      this.deleteUploadedImages(projectDocRef.id);
      throw error;
    }
  }
}

export default Project;
